import type { SvgIconProps } from "@mui/material/SvgIcon";
import { Bot, ScanSearch, UserSearch, Zap } from "lucide-mui";
import type { ReactElement } from "react";
import type { OrgAgentRun, OrgLiveRun } from "../../../api/client.ts";
import { pathForTenant } from "../../../contexts/TenantContext.tsx";
import { formatNumber, humanize } from "../../../utils/format.ts";

/** An agent run's kind (`agent_runs.agent_type`), as people read it. */
export function agentTypeLabel(type: string): string {
  switch (type) {
    case "prospecting":
      return "Prospecting";
    case "signal_research":
      return "Signal research";
    case "contact_research":
      return "Contact research";
    case "custom":
      return "Custom agent";
    default:
      return humanize(type);
  }
}

export function agentTypeIcon(type: string, props?: SvgIconProps): ReactElement {
  switch (type) {
    case "prospecting":
      return <ScanSearch {...props} />;
    case "signal_research":
      return <Zap {...props} />;
    case "contact_research":
      return <UserSearch {...props} />;
    default:
      return <Bot {...props} />;
  }
}

/** One colour per agent kind, for the part-to-whole bars. */
export function agentTypeColor(type: string): string {
  switch (type) {
    case "prospecting":
      return "var(--mui-palette-primary-main)";
    case "signal_research":
      return "var(--mui-palette-warning-main)";
    case "contact_research":
      return "var(--mui-palette-info-main)";
    default:
      return "var(--mui-palette-secondary-main)";
  }
}

// What a tool call reads as on the dashboard. Tool names are the short form
// the transcript stores (`mcp__hive__search_companies` → `search_companies`).
const TOOL_PHRASES: [RegExp, string][] = [
  [/^(WebSearch|web_search|search_web)$/i, "Searching the web"],
  [/^(WebFetch|render_page|read_document|fetch_url)$/i, "Reading"],
  [/save_attachment/i, "Saving a document"],
  [/^search_companies$|^get_filter_options$/, "Searching companies"],
  [/^get_company/, "Looking into a company"],
  [/contact|people|person/i, "Looking for people"],
  [/add_companies|add_to_list|add_.*prospect/i, "Adding companies to a collection"],
  [/job_ad/i, "Reading job ads"],
  [/regulatory|filing/i, "Reading filings"],
  [/tender/i, "Reading tenders"],
  [/news|press/i, "Reading the news"],
  [/financ/i, "Checking financials"],
  [/notion/i, "Reading Notion"],
  // The contact-data sources' tools, prefixed with their key (`clay_budget`, `getleads_quote`).
  [/_(budget|quote|purchase_status|lookup_company)$/, "Looking up contact data"],
  [/feedback/i, "Reading reviewer feedback"],
  [/^submit|result|brief|verdict/i, "Writing up the findings"],
  [/^(Task|Agent)$/, "Handing a task to a helper"],
];

/** What a running agent is doing right now: a verb, and the query, site or words behind it. */
export function liveActivity(run: Pick<OrgLiveRun, "activity_kind" | "activity_tool" | "activity_detail">): {
  verb: string;
  detail: string | null;
  quote: boolean;
} {
  if (run.activity_kind === "assistant_text") {
    return { verb: "Thinking", detail: run.activity_detail, quote: true };
  }
  if (run.activity_kind === "tool_use" && run.activity_tool) {
    const tool = run.activity_tool;
    const phrase = TOOL_PHRASES.find(([pattern]) => pattern.test(tool))?.[1] ?? humanize(tool);
    return { verb: phrase, detail: run.activity_detail, quote: false };
  }
  return { verb: "Getting started", detail: null, quote: false };
}

/** Where a run's work can be seen: its order, the signal's company in the inbox, or its schedule workflow. */
export function runLink(
  run: Pick<OrgAgentRun, "prospect_order_id" | "signal_id" | "schedule_workflow_id">,
  tenantSlug: string | undefined,
): string | undefined {
  if (run.prospect_order_id) return `/organization/orders/${run.prospect_order_id}`;
  if (run.schedule_workflow_id && tenantSlug) {
    return pathForTenant(tenantSlug, `/workflows/${run.schedule_workflow_id}`);
  }
  if (run.signal_id && tenantSlug) {
    return pathForTenant(tenantSlug, `/prospects/inbox?show=all&kind=signal&signal=${run.signal_id}`);
  }
  return undefined;
}

/** "45 min", "3.5 h", "120 h": time an agent spent researching. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return seconds > 0 ? "<1 min" : "0 min";
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  return `${hours < 10 ? hours.toFixed(1) : formatNumber(Math.round(hours))} h`;
}
